import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { getCustomList, getTranslationValidationHandling } from 'Hooks/query-hooks/helper';
import { IdSchema } from 'Components/MerchantOverviewModal/schema';
import { LIST_TYPES, MERCHANT_OPTIONS } from 'Common/enums';
import { MerchantOption } from '@brighterion/types/listEntry';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import {
    LIST_EXPIRY_END_TIME_MINUTES_OFFSET,
    LIST_EXPIRY_START_TIME_MINUTES_OFFSET,
    MAX_COMMENT_LENGTH,
    MAX_MERCHANT_ID_LENGTH,
    GENERAL_MAX_STRING_VALUE_LENGTH,
    GENERAL_MIN_STRING_VALUE_LENGTH
} from '@brighterion/utils/constants';
import { addMinutes, timeZone } from '@brighterion/utils/date-timezone';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';

type ListEntryFormSchema = {
    listType: SingleListType;
    t: TFunction;
    customListType?: string;
    schemaToOmit?: ('value' | 'merchantId' | 'comment' | 'startTime' | 'endTime')[];
};

const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
};

const startTimeSchema = (t: TFunction, locale?: string) => {
    return Yup.date()
        .optional()
        .typeError(() => t('listExpiryDate.invalid'))
        .test({
            name: 'test_startTime_against_endTime',
            test: (startTime, ctx) => {
                if (startTime) {
                    const startDateTime = new Date(startTime as Date).getTime();
                    const endDateTime = new Date(ctx.parent.endTime).getTime();
                    const plusFiveMinutesFromNow = addMinutes(
                        new Date(),
                        LIST_EXPIRY_START_TIME_MINUTES_OFFSET - 1
                    ).getTime();

                    if (startDateTime >= endDateTime) {
                        return ctx.createError({ message: t('listExpiryDate.maxStartTime') });
                    }

                    // when selecting a custom startDateTime, it should be 5 minutes from now
                    if (startDateTime < plusFiveMinutesFromNow) {
                        return ctx.createError({
                            message: t('listExpiryDate.minDate', {
                                date: utcToZonedTime(
                                    addMinutes(new Date(), LIST_EXPIRY_START_TIME_MINUTES_OFFSET),
                                    timeZone
                                ).toLocaleString(locale, dateTimeFormatOptions)
                            })
                        });
                    }
                }
                return true;
            }
        });
};

const endTimeSchema = (t: TFunction, locale?: string) => {
    return Yup.date()
        .optional()
        .typeError(() => t('listExpiryDate.invalid'))
        .test({
            name: 'test_endTime_against_startTime',
            test: (endTime, ctx) => {
                if (endTime) {
                    const startDateTime = new Date(ctx.parent.startTime as Date).getTime();
                    const endDateTime = new Date(endTime).getTime();
                    const plusTenMinutesFromNow = addMinutes(
                        new Date(),
                        LIST_EXPIRY_END_TIME_MINUTES_OFFSET - 1
                    ).getTime();

                    // endTime should be ahead of startTime
                    if (startDateTime >= endDateTime) {
                        return ctx.createError({ message: t('listExpiryDate.minEndTime') });
                    }

                    // when selecting a custom endDateTime, it must start 10 minutes from now
                    if (endDateTime < plusTenMinutesFromNow - 1) {
                        return ctx.createError({
                            message: t('listExpiryDate.minDate', {
                                date: utcToZonedTime(
                                    addMinutes(new Date(), LIST_EXPIRY_END_TIME_MINUTES_OFFSET),
                                    timeZone
                                ).toLocaleString(locale, dateTimeFormatOptions)
                            })
                        });
                    }
                }

                return true;
            }
        });
};

const merchantIdSchema = (t: TFunction) => {
    const validationT = getTranslationValidationHandling(t);
    return Yup.string()
        .trim(validationT('noLeadingTrailingSpaces'))
        .max(MAX_MERCHANT_ID_LENGTH)
        .when('merchantOption', {
            is: (merchantOption: Array<MerchantOption>) => {
                return merchantOption[0] !== MERCHANT_OPTIONS.ALL_MERCHANTS;
            },
            then: IdSchema(t)
        });
};

const commentSchema = (t: TFunction) => {
    const validationT = getTranslationValidationHandling(t);
    return Yup.string().optional().max(MAX_COMMENT_LENGTH).trim(validationT('noLeadingTrailingSpaces')).strict();
};

const basicValueSchema = (t: TFunction) => {
    const validationT = getTranslationValidationHandling(t);
    return Yup.string()
        .required(validationT('required', { field: 'Value' }))
        .trim(validationT('noLeadingTrailingSpaces'))
        .strict()
        .min(GENERAL_MIN_STRING_VALUE_LENGTH)
        .max(
            GENERAL_MAX_STRING_VALUE_LENGTH,
            validationT('maxLength', { field: 'Value', charCount: GENERAL_MAX_STRING_VALUE_LENGTH })
        );
};

// For Range Lists
const basicFromSchema = (t: TFunction) => {
    const validationT = getTranslationValidationHandling(t);
    return Yup.string()
        .required('')
        .trim(validationT('noLeadingTrailingSpaces'))
        .strict()
        .max(
            GENERAL_MAX_STRING_VALUE_LENGTH,
            validationT('maxLength', { field: 'From', charCount: GENERAL_MAX_STRING_VALUE_LENGTH })
        )
        .test('from', validationT('Enter numerical values only'), (value) => (value ? /^\d+$/.test(value) : true));
};

// For Range Lists
const basicToSchema = (t: TFunction) => {
    const validationT = getTranslationValidationHandling(t);
    return Yup.string()
        .required('')
        .trim(validationT('noLeadingTrailingSpaces'))
        .strict()
        .max(
            GENERAL_MAX_STRING_VALUE_LENGTH,
            validationT('maxLength', { field: 'To', charCount: GENERAL_MAX_STRING_VALUE_LENGTH })
        )
        .test('to', validationT('Enter numerical values only'), (value) => (value ? /^\d+$/.test(value) : true));
};

const commonSchemas = (t: TFunction, locale?: string) => {
    return {
        merchantId: merchantIdSchema(t),
        comment: commentSchema(t),
        startTime: startTimeSchema(t, locale), // introduced post 10.7.5
        endTime: endTimeSchema(t, locale) // introduced post 10.7.5
    };
};

export const getCustomListSchema = (customListType: string, t: TFunction) => {
    const { systemListType, validationDetails } = getCustomList(customListType);
    const validationT = getTranslationValidationHandling(t);
    const { locale } = useTemplateStore.getState().template;

    switch (systemListType) {
        case LIST_TYPES.GENERAL: {
            const { value } = validationDetails;
            const regexValue = value?.regex ? value.regex : '';
            return Yup.object().shape({
                value: basicValueSchema(t).matches(
                    new RegExp(regexValue),
                    value?.errMessage || validationT('defaultErrorMessageCustumList')
                ),
                ...commonSchemas(t, locale)
            });
        }
        case LIST_TYPES.PAN: {
            return Yup.object().shape({
                isValid: Yup.boolean().isTrue(),
                ...commonSchemas(t, locale)
            });
        }
        case LIST_TYPES.RANGE: {
            const { from, to } = validationDetails;
            const fromRegexValue = from?.regex ? from.regex : '';
            const toRegexValue = to?.regex ? to.regex : '';

            return Yup.object().shape({
                from: basicFromSchema(t).matches(
                    new RegExp(fromRegexValue),
                    from?.errMessage || validationT('defaultErrorMessageCustumList')
                ),

                to: basicToSchema(t).matches(
                    new RegExp(toRegexValue),
                    to?.errMessage || validationT('defaultErrorMessageCustumList')
                ),
                ...commonSchemas(t, locale)
            });
        }
        default:
            return Yup.object().shape({});
    }
};

export const getBaseSchema = (listType: SingleListType, t: TFunction) => {
    const { locale } = useTemplateStore.getState().template;
    switch (listType) {
        case LIST_TYPES.GENERAL: {
            return Yup.object().shape({
                value: basicValueSchema(t),
                ...commonSchemas(t, locale)
            });
        }
        case LIST_TYPES.PAN: {
            return Yup.object().shape({
                isValid: Yup.boolean().isTrue(),
                ...commonSchemas(t, locale)
            });
        }

        case LIST_TYPES.RANGE: {
            return Yup.object().shape({
                from: basicFromSchema(t),
                to: basicToSchema(t),
                ...commonSchemas(t, locale)
            });
        }

        default:
            return Yup.object().shape({});
    }
};

export const getListEntryFormSchema = ({ customListType, listType, t, schemaToOmit }: ListEntryFormSchema) => {
    const schema = customListType ? getCustomListSchema(customListType, t) : getBaseSchema(listType, t);
    return schema.omit(schemaToOmit ?? []);
};
